import React, { useEffect, useState } from "react";
import { ErrorBox, Field, Options, UserOptions } from "./common";
import { errorMessage, listToText, locationsToText, nullIfBlank, numOrNull } from "../dmApi";

function initialForm(project) {
  return {
    Tenant_Project_Id: project?.Tenant_Project_Id ? String(project.Tenant_Project_Id) : "",
    Project_Name: project?.Project_Name || "",
    Short_Code: project?.Short_Code || "",
    Priority: project?.Priority || "P2",
    Stage: project?.Stage || "Content",
    Location: project?.Location || "",
    Keywords: listToText(project?.Keywords),
    Target_Locations: locationsToText(project?.Target_Locations),
    Start_Date: project?.Start_Date || "",
    End_Date: project?.End_Date || "",
    Assigned_To: project?.Assigned_To ? String(project.Assigned_To) : "",
    Prepared_By: project?.Prepared_By ? String(project.Prepared_By) : "",
    Reviewed_By: project?.Reviewed_By ? String(project.Reviewed_By) : "",
    Description: project?.Description || "",
  };
}

/** Form state and save for a DM project. Staff pass ``tenantId``; portal users act on their own tenant. */
export function useProjectForm({ api, project, tenantId, onSaved }) {
  const [form, setForm] = useState(() => initialForm(project));
  const [tenantProjects, setTenantProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const effectiveTenantId = project?.tenant_id || tenantId;

  useEffect(() => {
    let cancelled = false;
    if (api.isStaff && !effectiveTenantId) return undefined;
    api
      .overview(api.isStaff ? effectiveTenantId : undefined)
      .then((data) => {
        if (!cancelled) setTenantProjects(data?.Tenant_Projects || []);
      })
      .catch(() => {
        if (!cancelled) setTenantProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, effectiveTenantId]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onLinkTenantProject = (e) => {
    const id = e.target.value;
    const linked = tenantProjects.find((p) => String(p.Tenant_Project_Id) === id);
    setForm((prev) => ({
      ...prev,
      Tenant_Project_Id: id,
      Project_Name: prev.Project_Name || linked?.Project_Name || "",
      Short_Code: prev.Short_Code || linked?.Short_Code || "",
      Location: prev.Location || linked?.Address || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.Project_Name.trim()) {
      setError("Project name is required");
      return;
    }
    setSaving(true);
    setError("");
    const body = {
      Tenant_Project_Id: numOrNull(form.Tenant_Project_Id),
      Project_Name: form.Project_Name.trim(),
      Short_Code: nullIfBlank(form.Short_Code.trim()),
      Priority: form.Priority,
      Stage: form.Stage,
      Location: nullIfBlank(form.Location.trim()),
      Keywords: form.Keywords,
      Target_Locations: form.Target_Locations,
      Start_Date: nullIfBlank(form.Start_Date),
      End_Date: nullIfBlank(form.End_Date),
      Assigned_To: numOrNull(form.Assigned_To),
      Prepared_By: numOrNull(form.Prepared_By),
      Reviewed_By: numOrNull(form.Reviewed_By),
      Description: nullIfBlank(form.Description),
    };
    try {
      const saved = project
        ? await api.updateProject(project.Dm_Project_Id, body)
        : await api.createProject({ ...body, tenant_id: api.isStaff ? effectiveTenantId : undefined });
      onSaved(saved);
    } catch (err) {
      setError(errorMessage(err, "Could not save project"));
    } finally {
      setSaving(false);
    }
  };

  return { form, set, onLinkTenantProject, tenantProjects, saving, error, setError, submit };
}

/** Create / edit a DM project. Staff pass ``tenantId``; portal users act on their own tenant. */
function ProjectForm({ api, meta, project, tenantId, onSaved, onCancel }) {
  const { form, set, onLinkTenantProject, tenantProjects, saving, error, submit } = useProjectForm({
    api,
    project,
    tenantId,
    onSaved,
  });

  return (
    <form onSubmit={submit}>
      <ErrorBox message={error} />
      <div className="dmp-form-grid">
        {tenantProjects.length > 0 ? (
          <Field label="Link to client's project / property">
            <select value={form.Tenant_Project_Id} onChange={onLinkTenantProject}>
              <option value="">Not linked</option>
              {tenantProjects.map((p) => (
                <option key={p.Tenant_Project_Id} value={p.Tenant_Project_Id}>
                  {p.Project_Name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Project name *">
          <input required value={form.Project_Name} onChange={set("Project_Name")} placeholder="e.g. Eastern Pride" />
        </Field>
        <Field label="Short code">
          <input value={form.Short_Code} onChange={set("Short_Code")} maxLength={20} />
        </Field>
        <Field label="Priority">
          <select value={form.Priority} onChange={set("Priority")}>
            <Options values={meta?.priorities} />
          </select>
        </Field>
        <Field label="Stage">
          <select value={form.Stage} onChange={set("Stage")}>
            <Options values={meta?.stages} />
          </select>
        </Field>
        <Field label="Property / business location">
          <input value={form.Location} onChange={set("Location")} placeholder="Dhandumailaram, Hyderabad" />
        </Field>
        <Field label="Start date">
          <input type="date" value={form.Start_Date} onChange={set("Start_Date")} />
        </Field>
        <Field label="End date">
          <input type="date" value={form.End_Date} onChange={set("End_Date")} />
        </Field>
        <Field label="Assigned to">
          <select value={form.Assigned_To} onChange={set("Assigned_To")}>
            <UserOptions users={meta?.users} />
          </select>
        </Field>
        <Field label="Prepared by">
          <select value={form.Prepared_By} onChange={set("Prepared_By")}>
            <UserOptions users={meta?.users} blank="—" />
          </select>
        </Field>
        <Field label="Reviewed by">
          <select value={form.Reviewed_By} onChange={set("Reviewed_By")}>
            <UserOptions users={meta?.users} blank="—" />
          </select>
        </Field>
        <Field label="Keywords (comma separated)" full>
          <input value={form.Keywords} onChange={set("Keywords")} placeholder="villas in hyderabad, gated community" />
        </Field>
        <Field label="Target locations (comma separated, optional radius e.g. LB Nagar (R2km))" full>
          <input
            value={form.Target_Locations}
            onChange={set("Target_Locations")}
            placeholder="LB Nagar (R2km), BN Reddy (R3km), Hayatnagar"
          />
        </Field>
        <Field label="Description / brief" full>
          <textarea rows={3} value={form.Description} onChange={set("Description")} />
        </Field>
      </div>
      <div className="dmp-form-actions">
        {onCancel ? (
          <button type="button" className="dmp-btn dmp-btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="dmp-btn" disabled={saving}>
          {saving ? "Saving…" : project ? "Save changes" : "Create project"}
        </button>
      </div>
    </form>
  );
}

export default ProjectForm;
